import React from "react";
import { useTranslation } from "react-i18next";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CTable2 from "../../../basecomp/components/CTable2";
import CButton from "../../../basecomp/components/CButton3";
import CDropDown2 from "../../../basecomp/components/CDropDown";
import CInput2 from "../../../basecomp/components/CInput2";
import PrintingVM from "../viewmodel/PrintingVM";
import CCarousel from "../../../basecomp/components/CCarousel";
import { AddPrintKeys } from "../../assets/VarConstants";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import CDropDown from "../../../basecomp/components/CDropDown_label_printing";
import { Box } from "@mui/system";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import LinearProgress from "@mui/material/LinearProgress";

const PrintingView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    poOptions,
    matOptions,
    sizeOptions,
    sLocOptions,
    selectedSize,
    selectedSLoc,
    stickerData,
    handleStickerDataEdit,
    buttonJson,
    labelData,
    handleSizeChange,
    handleStorageLocationChange,
    handleButtonClick,
    infoHeadData,
    infoRowData,
    carouselImages,
    openCarousel,
    setOpenCarousel,
    isPrinting,
    forceInput,
    handelUserTyping,
    duplicateDialog,
    handleDuplicateClose,
    isCheckingBin,
    handleDuplicateConfirm,
  } = PrintingVM();

  return (
    <Grid
      item
      xs={10}
      sm={10}
      md={10}
      lg={10}
      xl={10}
      sx={{ margin: "6em 0.5em 0.5em 0.5em" }}
    >
      <Card sx={{ padding: isXs ? "1em .5em" : "1em 2.5em" }}>
        <CBackDrop cOpen={isPrinting} />

        <Grid
          container
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {poOptions && poOptions.length > 0 && !forceInput ? (
            <CDropDown
              cHelperText={t("purchase_order")}
              cData={poOptions}
              cOnChange={(value) =>
                handleStickerDataEdit(AddPrintKeys.PO_KEY)(value, "dropdown")
              }
              cFullWidth={true}
              onUserTyping={handelUserTyping}
            />
          ) : (
            <CInput2
              cHintText={t("purchase_order")}
              cValue={stickerData[AddPrintKeys.PO_KEY]}
              cOnChange={(value) =>
                handleStickerDataEdit(AddPrintKeys.PO_KEY)(value, "input")
              }
              cFullWidth={true}
            />
          )}
          {matOptions && matOptions.length > 0 ? (
            <CDropDown
              cHelperText={t("material_number")}
              cData={matOptions}
              cOnChange={(value) =>
                handleStickerDataEdit(AddPrintKeys.MAT_NO_KEY)(
                  value,
                  "dropdown",
                )
              }
              cFullWidth={true}
              cIsRequired={true}
            />
          ) : (
            <CInput2
              cHintText={t("material_number")}
              cValue={stickerData[AddPrintKeys.MAT_NO_KEY]}
              cOnChange={(value) =>
                handleStickerDataEdit(AddPrintKeys.MAT_NO_KEY)(value, "input")
              }
              cFullWidth={true}
              cIsRequired={true}
            />
          )}
          <Box sx={{ display: "flex", width: "100%" }}>
            <CDropDown2
              cHelperText={t("size")}
              cData={sizeOptions}
              cIsOptionName="size"
              cOnChange={handleSizeChange}
              cFullWidth={true}
              cIsRequired={true}
              cDefaultValue={selectedSize}
            />
            <CDropDown2
              cHelperText={t("Storage Location")}
              cData={sLocOptions}
              cIsOptionName="sloc"
              cOnChange={handleStorageLocationChange}
              cFullWidth={true}
              cIsRequired={true}
              cDefaultValue={selectedSLoc}
            />
          </Box>
          <CInput2
            cHintText={t("quantity")}
            cValue={stickerData[AddPrintKeys.QUANTITY_KEY]}
            cOnChange={(value) =>
              handleStickerDataEdit(AddPrintKeys.QUANTITY_KEY)(value)
            }
            cFullWidth={true}
            cIsRequired={true}
            cType="quantity"
          />

          <Grid
            container
            spacing={2}
            marginBottom={8}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {Object.keys(buttonJson).map((key) => (
              <Grid item key={key}>
                <CButton
                  cSrc={buttonJson[key].src}
                  cTooltipText={buttonJson[key].tooltipText}
                  cOnClick={() => handleButtonClick(key)}
                  cFullWidth={true}
                  cDisabled={buttonJson[key].isDisabled}
                />
              </Grid>
            ))}
          </Grid>

          {labelData.length > 0 && (
            <CTable2
              cHeaderData={infoHeadData}
              cRowData={infoRowData}
              cActualData={labelData}
              cIsPagination={true}
              cSmallSize={true}
            />
          )}
          {openCarousel && (
            <CCarousel
              cOpen={openCarousel}
              images={carouselImages}
              cClose={() => setOpenCarousel(false)}
            />
          )}
        </Grid>

        <Fade in={isCheckingBin} unmountOnExit>
          <Paper
            elevation={6}
            sx={{
              position: "fixed",
              top: { xs: 72, sm: 84 },
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: (theme) => theme.zIndex.snackbar,
              borderRadius: 3,
              overflow: "hidden",
              minWidth: 260,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2.5,
                py: 1.5,
              }}
            >
              <CircularProgress size={22} thickness={5} />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Checking bin location
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Looking for duplicates...
                </Typography>
              </Box>
            </Box>
            <LinearProgress sx={{ height: 3 }} />
          </Paper>
        </Fade>
        <Dialog
          open={duplicateDialog.open}
          onClose={handleDuplicateClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Duplicate Bin Location</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              This bin location is already assigned to other materials.
            </Typography>
            <Typography>
              <b>Bin:</b> {duplicateDialog.binLocation}
            </Typography>
            <Typography gutterBottom>
              <b>Material :</b> {duplicateDialog.materials.join(", ")}
            </Typography>
            <Typography sx={{ mt: 1 }}>
              Are you sure you want to save this bin location?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDuplicateClose}>No</Button>
            <Button variant="contained" onClick={handleDuplicateConfirm}>
              Yes, Save
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Grid>
  );
};

export default PrintingView;
